import { useRef, useState } from 'react';

import { Input } from './Input';
import { Popover } from './Popover';
import { useDebounce } from '@/hooks/use-debounce';
import {
  useClientesLookup,
  useClienteLookupItem,
  type ClienteLookupItem,
} from '@/lib/clientes-v2-store';

interface ClienteV2ComboboxProps {
  id?: string;
  /** ID do cliente V2 selecionado ('' = nenhum). */
  value: string;
  onChange: (clienteId: string) => void;
  placeholder?: string;
  error?: string;
  /**
   * Porta opcional (OS do sistema, 25/10/2026): só cadastros com a etiqueta `Cliente`. Sem ela, nada muda para quem já
   * usava o seletor (Comercial, Compras).
   */
  soClientes?: boolean;
}

/**
 * Combobox de cliente da tabela canônica `clientes` (V2 / Omie) — busca em
 * `GET /api/v1/clientes/lookup`. (O antigo `ClientCombobox`, que lia a tabela
 * legada `clients` V1 e devolvia ids `client-…` incompatíveis com as FKs v2,
 * foi removido na Fase E3 da exclusão do comercial v1.)
 *
 * Usado no card do Comercial (Projetos 2.0 Parte 2 / P1) e na Solicitação de
 * Compra: garante que a venda carregue um `cliente_id` válido pra automação
 * deal→projeto herdar.
 *
 * S-2 (SEGURANCA-ACESSO-2): trocou `GET /clientes` (lista completa, agora atrás
 * da chave `clientes.ver`) pelo `/lookup` (id + nome, só login). É o que mantém
 * Compras — que não tem a chave — escolhendo cliente sem enxergar o cadastro.
 */
export function ClienteV2Combobox({
  id,
  value,
  onChange,
  placeholder = 'Buscar cliente por nome ou CNPJ…',
  error,
  soClientes = false,
}: ClienteV2ComboboxProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebounce(query, 300);

  const { data: busca, isFetching } = useClientesLookup({
    search: debouncedQuery || undefined,
    limit: 20,
    soClientes,
  });
  const resultados = busca?.items ?? [];
  const { data: selecionadoResp } = useClienteLookupItem(value || null);
  const selecionado = selecionadoResp?.items[0];

  const displayValue = open ? query : selecionado?.nome ?? '';

  function commit(c: ClienteLookupItem) {
    onChange(c.id);
    setQuery('');
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        id={id}
        role="combobox"
        aria-expanded={open}
        autoComplete="off"
        value={displayValue}
        placeholder={placeholder}
        error={error}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          setOpen(true);
          setQuery(e.target.value);
        }}
      />
      {/* CENSO-FLUTUANTE (05/08): era `absolute` cego — clipado pelo painel do
          modal (Nova proposta / Editar proposta, o print do Matheus). Migrado
          pro <Popover> canônico: portal + flip/shift, nada de z-* solto. */}
      <Popover
        aberto={open}
        gatilhoRef={containerRef}
        onFechar={() => {
          setOpen(false);
          setQuery('');
        }}
        larguraDoGatilho
        className="rounded-xl border border-line dark:border-line-dark bg-paper-surface dark:bg-paper-dark-surface shadow-lg"
      >
        <ul role="listbox" className="max-h-72 overflow-y-auto">
          {isFetching && resultados.length === 0 && (
            <li className="px-4 py-2 text-xs text-ink-3 dark:text-ink-dark-3 italic">Buscando…</li>
          )}
          {!isFetching && resultados.length === 0 && (
            <li className="px-4 py-2 text-xs text-ink-3 dark:text-ink-dark-3 italic">
              {debouncedQuery.trim()
                ? 'Nenhum cliente encontrado'
                : 'Digite pra buscar (ou veja os primeiros 20)'}
            </li>
          )}
          {resultados.map((c) => (
            <li
              key={c.id}
              role="option"
              aria-selected={c.id === value}
              onMouseDown={(e) => {
                e.preventDefault();
                commit(c);
              }}
              className="cursor-pointer px-4 py-2 text-sm text-ink-1 dark:text-ink-dark-1 hover:bg-paper-muted dark:hover:bg-paper-dark-muted"
            >
              {c.nome}
            </li>
          ))}
        </ul>
      </Popover>
    </div>
  );
}
